/**
 * Advanced Chat Widget Component - BGV Style
 * React Component wrapper for the Advanced Chat Widget
 */

/* eslint-disable no-undef */
import React, { useEffect, useRef } from 'react';
import { getValue } from '../globals/utils';
import { chatApiUrl, chatApiKey } from '../globals/constants';
import { escapeHtml, safeSetTextContent, safeCreateElement, validateAndSanitizeInput } from '../utils/SafeDOMUtil';

(function() {
    'use strict';
    
    // Load marked library if not already loaded
    // @ts-ignore
    if (!window.marked) {
        const markedScript = document.createElement('script');
        markedScript.src = "https://cdn.jsdelivr.net/npm/marked/marked.min.js";
        document.head.appendChild(markedScript);
    }
    
    class AdvancedChatWidget {
        constructor(config = {}) {
            // Normalize welcome message (treat empty, null, "null" as no value)
            const rawWelcome = typeof config.welcomeMessage === 'string'
            ? config.welcomeMessage.trim()
            : config.welcomeMessage;

            const safeWelcome =
            !rawWelcome || String(rawWelcome).toLowerCase() === 'null'
                ? this.getDefaultWelcomeMessage()
                : rawWelcome;

            this.config = {
            ...config, // spread first
            apiKey: config.apiKey || '',
            apiUrl: config.apiUrl || 'http://localhost:8000',
            title: config.title || 'AI Assistant',
            welcomeMessage: safeWelcome,              // our cleaned value overrides
            primaryColor: config.primaryColor || '#667eea',
            secondaryColor: config.secondaryColor || '#764ba2',
            quickActions: config.quickActions || this.getDefaultQuickActions(),
            jwtToken: config.jwtToken || null  // Add JWT token support
            };

            this.sessionId = 'session_' + Math.random().toString(36).substr(2, 9);
            this.currentChartType = 'bar';
            this.currentData = null;
            this.callInProgress = false;
            this.isMuted = false;
            this.currentCallId = null;

            this.init();
        }

        getDefaultWelcomeMessage() {
            return `👋 Hello! I'm your AI Assistant. How can I assist you today?`;
        }

        getDefaultQuickActions() {
            return [
                { icon: '🆕', text: 'Get Started', message: 'I want to get started' },
                { icon: '📊', text: 'Check Status', message: 'Check my status' },
                { icon: '📄', text: 'Documents', message: 'What documents are required?' },
                { icon: '💰', text: 'Pricing', message: 'Pricing information' },
                { icon: '❓', text: 'Help', message: 'Help' }
            ];
        }

        init() {
            // Check if widget already exists
            if (document.getElementById('acwWidgetContainer')) {
                console.warn('AdvancedChatWidget already initialized');
                return;
            }
            this.injectStyles();
            this.injectHTML();
            this.attachEventListeners();
        }

        destroy() {
            // Clean up widget
            const container = document.getElementById('acwWidgetContainer');
            if (container) {
                container.remove();
            }
            const styles = document.getElementById('acw-styles');
            if (styles) {
                styles.remove();
            }
        }

        injectStyles() {
            if (document.getElementById('acw-styles')) return;

            const styles = `
                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                /* Floating Chat Button */
                .acw-floating-btn {
                    position: fixed;
                    bottom: 70px;
                    right: 20px;
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, ${this.config.primaryColor} 0%, ${this.config.secondaryColor} 100%);
                    color: white;
                    border: none;
                    cursor: pointer;
                    box-shadow: 0 5px 20px rgba(102, 126, 234, 0.4);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 30px;
                    transition: all 0.3s;
                    z-index: 9996;
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                }
                    .acw-message-actions {
                        display: flex;
                        gap: 10px;
                        margin-top: 10px;
                    }

                    .acw-message-actions button {
                        background: #f3f4f6;
                        border: 1px solid #d1d5db;
                        padding: 6px 10px;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 14px;
                        transition: all 0.2s;
                    }

                    .acw-message-actions button:hover {
                        background: #e5e7eb;
                    }

                    .acw-action-like.liked {
                        background-color: #10b981;
                        color: white;
                    }

                    .acw-action-dislike.disliked {
                        background-color: #ef4444;
                        color: white;
                    }


                .acw-floating-btn:hover {
                    transform: scale(1.1);
                    box-shadow: 0 8px 30px rgba(102, 126, 234, 0.6);
                }

                .acw-floating-btn.hide {
                    display: none;
                }

                /* Chat Overlay - Right side panel */
                .acw-chat-overlay {
                    display: none;
                    position: fixed;
                    bottom: 100px;
                    right: 20px;
                    z-index: 9995;
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                }

                .acw-chat-overlay.show {
                    display: block;
                }

                .acw-chat-container {
                    width: 380px;
                    height: 550px;
                    max-height: calc(100vh - 120px);
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    animation: acw-slideUp 0.3s ease;
                }

                @keyframes acw-slideUp {
                    from {
                        transform: translateY(20px);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }

                /* Chat Header */
                .acw-chat-header {
                    background: linear-gradient(135deg, ${this.config.primaryColor} 0%, ${this.config.secondaryColor} 100%);
                    color: white;
                    padding: 12px 16px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .acw-header-left h1 {
                    font-size: 16px;
                    margin-bottom: 2px;
                }

                .acw-header-left .acw-status {
                    font-size: 12px;
                    opacity: 0.9;
                }

                .acw-status.online::before {
                    content: '●';
                    color: #10b981;
                    margin-right: 5px;
                }

                .acw-status.offline::before {
                    content: '●';
                    color: #ef4444;
                    margin-right: 5px;
                }

                .acw-header-right {
                    display: flex;
                    gap: 6px;
                }

                .acw-header-btn {
                    background: rgba(255, 255, 255, 0.2);
                    border: none;
                    color: white;
                    padding: 6px 8px;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    transition: all 0.2s;
                    font-family: inherit;
                }

                .acw-header-btn span {
                    display: none;
                }

                .acw-header-btn:hover {
                    background: rgba(255, 255, 255, 0.3);
                    transform: translateY(-2px);
                }

                .acw-call-btn {
                    background: #10b981;
                    border: none;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .acw-close-chat-btn {
                    background: rgba(239, 68, 68, 0.8);
                    border: none;
                }

                .acw-close-chat-btn:hover {
                    background: #ef4444;
                }

                /* Chat Main */
                .acw-chat-main {
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                    overflow: hidden;
                }

                /* Chat Messages */
                .acw-chat-messages {
                    flex: 1;
                    padding: 12px;
                    overflow-y: auto;
                    background: #f8f9fa;
                }

                .acw-chat-messages::-webkit-scrollbar {
                    width: 8px;
                }

                .acw-chat-messages::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                .acw-chat-messages::-webkit-scrollbar-thumb {
                    background: #cbd5e0;
                    border-radius: 4px;
                }

                .acw-message {
                    margin-bottom: 15px;
                    display: flex;
                    animation: acw-messageSlide 0.3s ease-in;
                }

                @keyframes acw-messageSlide {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .acw-message.user {
                    justify-content: flex-end;
                }

                .acw-message.bot {
                    justify-content: flex-start;
                }

                .acw-message-bubble {
                    max-width: 85%;
                    padding: 10px 14px;
                    border-radius: 16px;
                    word-wrap: break-word;
                    font-size: 14px;
                    line-height: 1.4;
                }

                .acw-message.user .acw-message-bubble {
                    background: linear-gradient(135deg, ${this.config.primaryColor} 0%, ${this.config.secondaryColor} 100%);
                    color: white;
                }

                .acw-message.bot .acw-message-bubble {
                    background: white;
                    color: #333;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }

                .acw-message.bot .acw-message-bubble ul {
                    margin-top: 10px;
                    padding-left: 20px;
                }

                .acw-sql-result {
                    margin-top: 10px;
                }

                .acw-sql-query {
                    color: #059669;
                    margin-bottom: 8px;
                }

                .acw-visualize-btn {
                    background: linear-gradient(135deg, ${this.config.primaryColor} 0%, ${this.config.secondaryColor} 100%);
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.3s;
                    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
                }

                .acw-visualize-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.5);
                }

                /* Visualization Panel */
                .acw-visualization-panel {
                    width: 100%;
                    border-top: 1px solid #e5e7eb;
                    background: white;
                    overflow-y: auto;
                    padding: 12px;
                    display: none;
                    max-height: 250px;
                }

                .acw-visualization-panel.show {
                    display: block;
                }

                .acw-viz-header {
                    font-size: 18px;
                    font-weight: 600;
                    margin-bottom: 15px;
                    color: #374151;
                }

                .acw-chart-type-selector {
                    display: flex;
                    gap: 8px;
                    margin-bottom: 15px;
                    flex-wrap: wrap;
                }

                .acw-chart-type-btn {
                    background: white;
                    border: 2px solid #e5e7eb;
                    padding: 8px 12px;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 12px;
                    transition: all 0.2s;
                }

                .acw-chart-type-btn:hover {
                    border-color: ${this.config.primaryColor};
                    color: ${this.config.primaryColor};
                }

                .acw-chart-type-btn.active {
                    background: ${this.config.primaryColor};
                    color: white;
                    border-color: ${this.config.primaryColor};
                }

                .acw-chart-container {
                    background: #f9fafb;
                    border-radius: 12px;
                    padding: 15px;
                    margin-bottom: 15px;
                }

                .acw-chart-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #6b7280;
                    margin-bottom: 10px;
                }

                /* Input Area */
                .acw-chat-input-container {
                    padding: 12px;
                    background: white;
                    border-top: 1px solid #e5e7eb;
                }

                .acw-quick-actions {
                    display: flex;
                    gap: 8px;
                    margin-bottom: 12px;
                    flex-wrap: wrap;
                }

                .acw-quick-action-btn {
                    background: #f3f4f6;
                    border: 1px solid #e5e7eb;
                    padding: 8px 12px;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 13px;
                    transition: all 0.2s;
                    font-family: inherit;
                }

                .acw-quick-action-btn:hover {
                    background: #e5e7eb;
                    transform: translateY(-2px);
                }

                .acw-input-row {
                    display: flex;
                    gap: 10px;
                }

                .acw-message-input {
                    flex: 1;
                    padding: 10px 14px;
                    border: 1px solid #e5e7eb;
                    border-radius: 20px;
                    font-size: 14px;
                    outline: none;
                    transition: border-color 0.3s;
                    font-family: inherit;
                }

                .acw-message-input:focus {
                    border-color: ${this.config.primaryColor};
                }

                .acw-send-btn {
                    background: linear-gradient(135deg, ${this.config.primaryColor} 0%, ${this.config.secondaryColor} 100%);
                    color: white;
                    border: none;
                    padding: 10px 18px;
                    border-radius: 20px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 600;
                    transition: transform 0.2s;
                    font-family: inherit;
                }

                .acw-send-btn:hover {
                    transform: scale(1.05);
                }

                .acw-options {
                    display: flex;
                    gap: 15px;
                    margin-top: 10px;
                    font-size: 13px;
                }

                .acw-option-label {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    cursor: pointer;
                }

                /* Call Modal */
                .acw-call-modal {
                    display: none;
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.7);
                    z-index: 10001;
                    justify-content: center;
                    align-items: center;
                }

                .acw-call-modal.show {
                    display: flex;
                }

                .acw-call-modal-content {
                    background: white;
                    padding: 30px;
                    border-radius: 20px;
                    text-align: center;
                    min-width: 300px;
                }

                .acw-call-avatar {
                    width: 100px;
                    height: 100px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, ${this.config.primaryColor} 0%, ${this.config.secondaryColor} 100%);
                    margin: 0 auto 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 48px;
                }

                .acw-call-status {
                    font-size: 18px;
                    color: #374151;
                    margin-bottom: 10px;
                }

                .acw-call-number {
                    font-size: 14px;
                    color: #6b7280;
                    margin-bottom: 30px;
                }

                .acw-call-actions {
                    display: flex;
                    gap: 15px;
                    justify-content: center;
                }

                .acw-call-action-btn {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    border: none;
                    cursor: pointer;
                    font-size: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: transform 0.2s;
                }

                .acw-call-action-btn:hover {
                    transform: scale(1.1);
                }

                .acw-call-end {
                    background: #ef4444;
                    color: white;
                }

                .acw-call-mute {
                    background: #f3f4f6;
                    color: #374151;
                }

                /* Responsive Design */
                @media (max-width: 480px) {
                    .acw-chat-overlay {
                        bottom: 0;
                        right: 0;
                        left: 0;
                        top: 0;
                    }

                    .acw-chat-container {
                        width: 100%;
                        height: 100%;
                        border-radius: 0;
                    }

                    .acw-visualization-panel {
                        width: 100%;
                        position: absolute;
                        top: 0;
                        right: 0;
                        height: 100%;
                        z-index: 10;
                    }

                    .acw-floating-btn {
                        bottom: 20px;
                        right: 20px;
                        width: 60px;
                        height: 60px;
                        font-size: 26px;
                    }
                }
            `;

            const styleSheet = document.createElement('style');
            styleSheet.id = 'acw-styles';
            styleSheet.textContent = styles;
            document.head.appendChild(styleSheet);

            // Load Plotly for charts
            // @ts-ignore
            if (!window.Plotly) {
                const plotlyScript = document.createElement('script');
                plotlyScript.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js';
                document.head.appendChild(plotlyScript);
            }
        }

        injectHTML() {
            const widgetHTML = `
                <!-- Floating Chat Button -->
                <button class="acw-floating-btn" id="acwFloatingBtn">
                        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <!-- Robot Head -->
                            <rect x="5" y="6" width="14" height="12" rx="3" stroke="white" stroke-width="1.5" fill="none"/>
                            <!-- Robot Eyes - glowing style -->
                            <rect x="7.5" y="9" width="3" height="2.5" rx="0.5" fill="white"/>
                            <rect x="13.5" y="9" width="3" height="2.5" rx="0.5" fill="white"/>
                            <!-- Mouth - digital display -->
                            <path d="M8 14.5h2M10.5 14.5h3M14 14.5h2" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
                            <!-- Antenna -->
                            <path d="M12 6V3.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
                            <circle cx="12" cy="2.5" r="1.2" fill="white"/>
                            <!-- Side panels/ears -->
                            <path d="M5 10H3.5M5 13H3.5M19 10h1.5M19 13h1.5" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
                            <!-- Chin detail -->
                            <path d="M10 18v1.5M14 18v1.5" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
                        </svg>
                    </button>

                <!-- Chat Overlay -->
                <div class="acw-chat-overlay" id="acwChatOverlay">
                    <div class="acw-chat-container">
                        <!-- Chat Header -->
                        <div class="acw-chat-header">
                            <div class="acw-header-left">
                                <h1>🤖 ${this.config.title}</h1>
                                <div class="acw-status online" id="acwStatusIndicator">Online</div>
                            </div>
                            <div class="acw-header-right">
                                <button class="acw-header-btn" id="acwToggleViz">📊 <span>Charts</span></button>
                                <button class="acw-header-btn" id="acwClearChat">🗑️ <span>Clear</span></button>
                                <button class="acw-header-btn acw-call-btn" id="acwCallBtn">📞 <span>Call Back</span></button>
                                <button class="acw-header-btn acw-close-chat-btn" id="acwCloseChat">✖ <span>Close</span></button>
                            </div>
                        </div>

                        <!-- Main Content -->
                        <div class="acw-chat-main">
                            <!-- Chat Messages -->
                            <div class="acw-chat-messages" id="acwChatMessages">
                                <div class="acw-message bot">
                                    <div class="acw-message-bubble">
                                        ${this.config.welcomeMessage}
                                    </div>
                                </div>
                            </div>

                            <!-- Visualization Panel -->
                            <div class="acw-visualization-panel" id="acwVisualizationPanel">
                                <div class="acw-viz-header">📊 Data Visualizations</div>
                                <div class="acw-chart-type-selector">
                                    <button class="acw-chart-type-btn active" data-chart-type="bar">📊 Bar</button>
                                    <button class="acw-chart-type-btn" data-chart-type="line">📈 Line</button>
                                    <button class="acw-chart-type-btn" data-chart-type="pie">🥧 Pie</button>
                                    <button class="acw-chart-type-btn" data-chart-type="scatter">⚫ Scatter</button>
                                    <button class="acw-chart-type-btn" data-chart-type="area">🏔️ Area</button>
                                    <button class="acw-chart-type-btn" data-chart-type="table">📋 Table</button>
                                </div>
                                <div id="acwChartContainers"></div>
                            </div>
                        </div>

                        <!-- Input Area -->
                        <div class="acw-chat-input-container">
                           
                            <div class="acw-input-row">
                                <input type="text" class="acw-message-input" id="acwMessageInput"
                                       placeholder="Type your message...">
                                <button class="acw-send-btn" id="acwSendBtn">Send</button>
                            </div>
                            
                        </div>
                    </div>
                </div>

                <!-- Call Modal -->
                <div class="acw-call-modal" id="acwCallModal">
                    <div class="acw-call-modal-content">
                        <div class="acw-call-avatar">🤖</div>
                        <div class="acw-call-status" id="acwCallStatus">Calling Support...</div>
                        <div class="acw-call-number" id="acwCallNumber">+1 (800) 123-4567</div>
                        <div class="acw-call-actions">
                            <button class="acw-call-action-btn acw-call-mute" id="acwCallMute">🎤</button>
                            <button class="acw-call-action-btn acw-call-end" id="acwCallEnd">📞</button>
                        </div>
                    </div>
                </div>
            `;

            // Create a dedicated container and inject the full widget HTML.
            // Using innerHTML here is acceptable because the template is a static,
            // hard‑coded string that we fully control (no user input).
            const widgetContainer = document.createElement('div');
            widgetContainer.id = 'acwWidgetContainer';
            widgetContainer.innerHTML = widgetHTML;
            document.body.appendChild(widgetContainer);
        }

        attachEventListeners() {
            // Floating button
            const floatingBtn = document.getElementById('acwFloatingBtn');
            if (floatingBtn) {
                floatingBtn.addEventListener('click', () => this.openChat());
            }

            // Close button
            const closeBtn = document.getElementById('acwCloseChat');
            if (closeBtn) {
                closeBtn.addEventListener('click', () => this.closeChat());
            }

            // Clear chat
            const clearBtn = document.getElementById('acwClearChat');
            if (clearBtn) {
                clearBtn.addEventListener('click', () => this.clearChat());
            }

            // Send message
            const sendBtn = document.getElementById('acwSendBtn');
            if (sendBtn) {
                sendBtn.addEventListener('click', () => this.sendMessage());
            }
            
            const messageInput = document.getElementById('acwMessageInput');
            if (messageInput) {
                messageInput.addEventListener('keypress', (e) => {
                    if (e.key === 'Enter') this.sendMessage();
                });
            }

            // Quick actions
            document.querySelectorAll('.acw-quick-action-btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    // @ts-ignore
                    const message = e.currentTarget.getAttribute('data-message');
                    if (message) this.quickAction(message);
                });
            });

            // Visualization toggle
            const toggleVizBtn = document.getElementById('acwToggleViz');
            if (toggleVizBtn) {
                toggleVizBtn.addEventListener('click', () => this.toggleVisualization());
            }

            // Chart type buttons
            document.querySelectorAll('.acw-chart-type-btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    // @ts-ignore
                    const type = e.currentTarget.getAttribute('data-chart-type');
                    // @ts-ignore
                    if (type) this.changeChartType(type, e.currentTarget);
                });
            });

            // Call functionality
            const callBtn = document.getElementById('acwCallBtn');
            if (callBtn) {
                callBtn.addEventListener('click', () => this.initiateCall());
            }
            
            const callEndBtn = document.getElementById('acwCallEnd');
            if (callEndBtn) {
                callEndBtn.addEventListener('click', () => this.endCall());
            }
            
            const callMuteBtn = document.getElementById('acwCallMute');
            if (callMuteBtn) {
                callMuteBtn.addEventListener('click', () => this.toggleMute());
            }

            // Close overlay on click outside
            const overlay = document.getElementById('acwChatOverlay');
            if (overlay) {
                overlay.addEventListener('click', (e) => {
                    // @ts-ignore
                    if (e.target && e.target.id === 'acwChatOverlay') {
                        this.closeChat();
                    }
                });
            }

            // Close call modal on click outside
            const callModal = document.getElementById('acwCallModal');
            if (callModal) {
                callModal.addEventListener('click', (e) => {
                    // @ts-ignore
                    if (e.target && e.target.id === 'acwCallModal') {
                        this.endCall();
                    }
                });
            }
        }


        openChat() {
            const overlay = document.getElementById('acwChatOverlay');
            const floatingBtn = document.getElementById('acwFloatingBtn');
            const messageInput = document.getElementById('acwMessageInput');
            
            if (overlay) overlay.classList.add('show');
            if (floatingBtn) floatingBtn.classList.add('hide');
            if (messageInput) messageInput.focus();
        }

        closeChat() {
            const overlay = document.getElementById('acwChatOverlay');
            const floatingBtn = document.getElementById('acwFloatingBtn');
            
            if (overlay) overlay.classList.remove('show');
            if (floatingBtn) floatingBtn.classList.remove('hide');
        }

        quickAction(message) {
            const messageInput = document.getElementById('acwMessageInput');
            if (messageInput) {
                // @ts-ignore
                messageInput.value = message;
                this.sendMessage();
            }
        }

        async sendMessage() {
            const input = document.getElementById('acwMessageInput');
            if (!input) return;
            
            // @ts-ignore
            const message = input.value.trim();
            if (!message) return;

            // Add user message to chat
            this.addMessage(message, 'user');
            // @ts-ignore
            input.value = '';

            // Show loading
            const loadingId = this.addMessage('Thinking...', 'bot');

            try {
                // Build headers with JWT if available
                const headers = {
                    'Content-Type': 'application/json'
                };

                // Add JWT token to Authorization header if available
                if (this.config.jwtToken) {
                    headers['Authorization'] = `Bearer ${this.config.jwtToken}`;
                    console.log('[CHAT] Using JWT authentication');
                }

                const response = await fetch(`${this.config.apiUrl}/api/chat/widget/message`, {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify({
                        tenant_api_key: this.config.apiKey,
                        session_id: this.sessionId,
                        message: message
                    })
                });

                const data = await response.json();

                // Remove loading message
                const loadingElement = document.getElementById(loadingId);
                if (loadingElement) loadingElement.remove();

                // Display bot response
                this.displayBotResponse(data);

            } catch (error) {
                const loadingElement = document.getElementById(loadingId);
                if (loadingElement) loadingElement.remove();
                this.addMessage(`Error: ${error.message}. Please make sure the server is running.`, 'bot');
            }
        }

        addMessage(text, sender) {
            const messagesDiv = document.getElementById('acwChatMessages');
            if (!messagesDiv) return null;
            
            const messageDiv = document.createElement('div');
            messageDiv.className = `acw-message ${sender}`;
            const messageId = 'acw-msg-' + sender + '-' + Date.now();

            messageDiv.id = messageId;

            const bubble = document.createElement('div');
            bubble.className = 'acw-message-bubble';

            // Use safe text content instead of innerHTML to prevent content spoofing
            if (typeof text === 'string') {
                // Validate and sanitize user input before display
                const validation = validateAndSanitizeInput(text, 5000);
                if (validation.isValid) {
                    safeSetTextContent(bubble, validation.sanitized);
                } else {
                    safeSetTextContent(bubble, 'Invalid message content');
                    console.warn('AgentChat: Invalid message content detected', validation.error);
                }
            } else {
                safeSetTextContent(bubble, String(text || ''));
            }

            messageDiv.appendChild(bubble);
            messagesDiv.appendChild(messageDiv);
            messagesDiv.scrollTop = messagesDiv.scrollHeight;

            return messageId;
        }

        displayBotResponse(data) {
            const messagesDiv = document.getElementById('acwChatMessages');
            if (!messagesDiv) return;
            
            const messageDiv = document.createElement('div');
            messageDiv.className = 'acw-message bot';

            const bubble = document.createElement('div');
            bubble.className = 'acw-message-bubble';

            // Add AI response
            if (data.response) {
                const responseText = document.createElement('div');

                // Validate and sanitize response before display
                const responseValidation = validateAndSanitizeInput(data.response, 10000);
                if (!responseValidation.isValid) {
                    console.warn('AgentChat: Invalid response content detected', responseValidation.error);
                    safeSetTextContent(responseText, 'Invalid response content');
                } else {
                    // Use marked to parse markdown if available, but we ultimately
                    // render plain text to avoid duplicate content from element/text nodes.
                    // @ts-ignore
                    if (window.marked && typeof window.marked.parse === 'function') {
                        try {
                            // @ts-ignore
                            const parsedHtml = window.marked.parse(responseValidation.sanitized);
                            const parser = new DOMParser();
                            const doc = parser.parseFromString(parsedHtml, 'text/html');
                            const plainText = doc.body.textContent || '';
                            safeSetTextContent(responseText, plainText);
                        } catch (e) {
                            // Fallback to plain text if parsing fails
                            safeSetTextContent(responseText, responseValidation.sanitized);
                        }
                    } else {
                        // Simple text replacement with safe handling
                        const safeText = responseValidation.sanitized
                            .replace(/\n/g, '\n')
                            .replace(/-\s+/g, '• ');
                        safeSetTextContent(responseText, safeText);
                    }
                }
                
                // Add action buttons (copy, like, dislike) - create safely
                const actions = document.createElement('div');
                actions.className = 'acw-message-actions';

                const copyBtn = safeCreateElement('button', { className: 'acw-action-copy' }, '📋');
                const likeBtn = safeCreateElement('button', { className: 'acw-action-like' }, '👍');
                const dislikeBtn = safeCreateElement('button', { className: 'acw-action-dislike' }, '👎');
                
                actions.appendChild(copyBtn);
                actions.appendChild(likeBtn);
                actions.appendChild(dislikeBtn);
                
                bubble.appendChild(responseText);
                bubble.appendChild(actions);              
                this.attachActionButtonEvents(bubble, responseText);
            }

            // Add SQL results if present (only show visualization button, not the query)
            if (data.sql_result && data.sql_result.success && data.sql_result.results && data.sql_result.results.length > 0) {
                const sqlDiv = document.createElement('div');
                sqlDiv.className = 'acw-sql-result';

                // Add visualization button
                const vizBtn = document.createElement('button');
                vizBtn.className = 'acw-visualize-btn';
                vizBtn.textContent = '📊 Visualize Data';
                vizBtn.onclick = () => this.visualizeData(data.sql_result.results, data.message || 'Data Visualization');
                sqlDiv.appendChild(vizBtn);

                // Auto-visualize if enabled
                const autoVizCheckbox = document.getElementById('acwAutoVisualize');
                // @ts-ignore
                if (autoVizCheckbox && autoVizCheckbox.checked) {
                    setTimeout(() => this.visualizeData(data.sql_result.results, data.message || 'Data Visualization'), 500);
                }

                bubble.appendChild(sqlDiv);
            }

            // Add sources if using RAG
            if (data.sources && data.sources.length > 0) {
                const sourcesDiv = document.createElement('div');
                sourcesDiv.style.fontSize = '12px';
                sourcesDiv.style.marginTop = '10px';
                sourcesDiv.style.color = '#6b7280';
                sourcesDiv.textContent = `📚 Sources: ${data.sources.length} documents from knowledge base`;
                bubble.appendChild(sourcesDiv);
            }

            messageDiv.appendChild(bubble);
            messagesDiv.appendChild(messageDiv);
            messagesDiv.scrollTop = messagesDiv.scrollHeight;
        }

        attachActionButtonEvents(bubble, responseText) {
            const copyBtn = bubble.querySelector('.acw-action-copy');
            const likeBtn = bubble.querySelector('.acw-action-like');
            const dislikeBtn = bubble.querySelector('.acw-action-dislike');
        
            if (copyBtn) {
                // Copy response
                copyBtn.addEventListener('click', () => {
                    navigator.clipboard.writeText(responseText.innerText);
                    copyBtn.textContent = "✔️";
                    setTimeout(() => copyBtn.textContent = "📋", 1000);
                });
            }
        
            if (likeBtn) {
                // Like
                likeBtn.addEventListener('click', () => {
                    likeBtn.classList.toggle('liked');
                    if (dislikeBtn) dislikeBtn.classList.remove('disliked');
                });
            }
        
            if (dislikeBtn) {
                // Dislike
                dislikeBtn.addEventListener('click', () => {
                    dislikeBtn.classList.toggle('disliked');
                    if (likeBtn) likeBtn.classList.remove('liked');
                });
            }
        }
        
        visualizeData(data, title) {
            this.currentData = data;
            const panel = document.getElementById('acwVisualizationPanel');
            if (panel) {
                panel.classList.add('show');
                this.renderChart(data, title, this.currentChartType);
            }
        }

        renderChart(data, title, chartType) {
            const container = document.getElementById('acwChartContainers');
            if (!container) return;
            
            // Clear container safely
            while (container.firstChild) {
                container.removeChild(container.firstChild);
            }
            
            // Create chart container safely
            const chartContainer = document.createElement('div');
            chartContainer.className = 'acw-chart-container';
            
            const chartTitle = document.createElement('div');
            chartTitle.className = 'acw-chart-title';
            const titleValidation = validateAndSanitizeInput(title || 'Data Visualization', 200);
            chartTitle.textContent = titleValidation.isValid ? titleValidation.sanitized : 'Data Visualization';
            
            const chartDiv = document.createElement('div');
            chartDiv.id = 'acwChart';
            chartDiv.style.width = '100%';
            chartDiv.style.height = '300px';
            
            chartContainer.appendChild(chartTitle);
            chartContainer.appendChild(chartDiv);
            container.appendChild(chartContainer);

            if (!data || data.length === 0) {
                // Clear and show message safely
                while (container.firstChild) {
                    container.removeChild(container.firstChild);
                }
                const noDataMsg = document.createElement('div');
                noDataMsg.style.padding = '20px';
                noDataMsg.style.textAlign = 'center';
                noDataMsg.textContent = 'No data to visualize';
                container.appendChild(noDataMsg);
                return;
            }

            // Wait for Plotly to load
            // @ts-ignore
            if (!window.Plotly) {
                setTimeout(() => this.renderChart(data, title, chartType), 500);
                return;
            }

            // Prepare data
            const keys = Object.keys(data[0]);
            const xKey = keys[0];
            const yKey = keys[1] || keys[0];

            const xValues = data.map(row => row[xKey]);
            const yValues = data.map(row => row[yKey]);

            // Render based on type
            const layout = {
                margin: {t: 20, b: 40, l: 40, r: 20},
                paper_bgcolor: '#f9fafb',
                plot_bgcolor: '#f9fafb'
            };

            // @ts-ignore
            const PlotlyLib = window.Plotly;
            switch(chartType) {
                case 'bar':
                    PlotlyLib.newPlot('acwChart', [{
                        x: xValues,
                        y: yValues,
                        type: 'bar',
                        marker: {color: this.config.primaryColor}
                    }], layout);
                    break;

                case 'line':
                    PlotlyLib.newPlot('acwChart', [{
                        x: xValues,
                        y: yValues,
                        type: 'scatter',
                        mode: 'lines+markers',
                        line: {color: this.config.primaryColor}
                    }], layout);
                    break;

                case 'pie':
                    PlotlyLib.newPlot('acwChart', [{
                        labels: xValues,
                        values: yValues,
                        type: 'pie'
                    }], layout);
                    break;

                case 'scatter':
                    PlotlyLib.newPlot('acwChart', [{
                        x: xValues,
                        y: yValues,
                        mode: 'markers',
                        type: 'scatter',
                        marker: {size: 12, color: this.config.primaryColor}
                    }], layout);
                    break;

                case 'area':
                    PlotlyLib.newPlot('acwChart', [{
                        x: xValues,
                        y: yValues,
                        fill: 'tozeroy',
                        type: 'scatter',
                        line: {color: this.config.primaryColor}
                    }], layout);
                    break;

                case 'table':
                    this.renderTable(data);
                    break;
            }
        }

        renderTable(data) {
            const chartElement = document.getElementById('acwChart');
            if (!chartElement) return;
            
            // Clear existing content safely
            while (chartElement.firstChild) {
                chartElement.removeChild(chartElement.firstChild);
            }
            
            // Create table safely using DOM methods
            const table = document.createElement('table');
            table.style.width = '100%';
            table.style.borderCollapse = 'collapse';
            
            const thead = document.createElement('thead');
            const headerRow = document.createElement('tr');
            
            const keys = Object.keys(data[0]);
            keys.forEach(key => {
                const th = document.createElement('th');
                th.style.background = this.config.primaryColor;
                th.style.color = 'white';
                th.style.padding = '8px';
                th.style.textAlign = 'left';
                // Validate and sanitize key before display
                const keyValidation = validateAndSanitizeInput(key, 100);
                th.textContent = keyValidation.isValid ? keyValidation.sanitized : String(key);
                headerRow.appendChild(th);
            });
            
            thead.appendChild(headerRow);
            table.appendChild(thead);
            
            const tbody = document.createElement('tbody');
            data.forEach(row => {
                const tr = document.createElement('tr');
                keys.forEach(key => {
                    const td = document.createElement('td');
                    td.style.padding = '8px';
                    td.style.borderBottom = '1px solid #e5e7eb';
                    // Validate and sanitize cell value before display
                    const cellValue = row[key];
                    const cellValidation = validateAndSanitizeInput(String(cellValue || ''), 500);
                    td.textContent = cellValidation.isValid ? cellValidation.sanitized : String(cellValue || '');
                    tr.appendChild(td);
                });
                tbody.appendChild(tr);
            });
            
            table.appendChild(tbody);
            chartElement.appendChild(table);
        }

        changeChartType(type, button) {
            this.currentChartType = type;

            // Update active button
            document.querySelectorAll('.acw-chart-type-btn').forEach(btn => {
                btn.classList.remove('active');
            });
            if (button) button.classList.add('active');

            // Re-render chart if data exists
            if (this.currentData) {
                this.renderChart(this.currentData, 'Data Visualization', type);
            }
        }

        toggleVisualization() {
            const panel = document.getElementById('acwVisualizationPanel');
            if (panel) {
                panel.classList.toggle('show');
            }
        }

        clearChat() {
            // Use window.confirm to avoid eslint no-restricted-globals warning
            if (window.confirm('Clear all messages?')) {
                const messagesDiv = document.getElementById('acwChatMessages');
                if (messagesDiv) {
                    // Clear existing content safely
                    while (messagesDiv.firstChild) {
                        messagesDiv.removeChild(messagesDiv.firstChild);
                    }
                    
                    // Create welcome message safely
                    const messageDiv = document.createElement('div');
                    messageDiv.className = 'acw-message bot';
                    
                    const bubble = document.createElement('div');
                    bubble.className = 'acw-message-bubble';
                    const welcomeMsg = this.config.welcomeMessage || this.getDefaultWelcomeMessage();
                    const welcomeValidation = validateAndSanitizeInput(welcomeMsg, 500);
                    safeSetTextContent(bubble, welcomeValidation.isValid ? welcomeValidation.sanitized : this.getDefaultWelcomeMessage());
                    
                    messageDiv.appendChild(bubble);
                    messagesDiv.appendChild(messageDiv);
                }

                // Clear visualization panel safely
                const chartContainers = document.getElementById('acwChartContainers');
                if (chartContainers) {
                    while (chartContainers.firstChild) {
                        chartContainers.removeChild(chartContainers.firstChild);
                    }
                }
                
                const vizPanel = document.getElementById('acwVisualizationPanel');
                if (vizPanel) vizPanel.classList.remove('show');
            }
        }

        async initiateCall() {
            // Prompt user for phone number
            const phoneNumber = prompt('Feel Free To Reach Out : 19785408346');

            if (!phoneNumber) {
                return;
            }

            // Prompt for name (optional)
            const clientName = prompt('Enter your name (optional):', 'Guest') || 'Guest';

            const modal = document.getElementById('acwCallModal');
            if (!modal) return;
            
            modal.classList.add('show');
            
            const callNumberEl = document.getElementById('acwCallNumber');
            if (callNumberEl) callNumberEl.textContent = phoneNumber;
            
            const callStatusEl = document.getElementById('acwCallStatus');
            if (callStatusEl) callStatusEl.textContent = 'Initiating call...';

            try {
                // Build headers with JWT if available
                const headers = {
                    'Content-Type': 'application/json'
                };

                // Add JWT token to Authorization header if available
                if (this.config.jwtToken) {
                    headers['Authorization'] = `Bearer ${this.config.jwtToken}`;
                    console.log('[CALLING] Using JWT authentication for voice call');
                }

                // Call the enhanced API with database access capabilities
                const response = await fetch(`${this.config.apiUrl}/api/enhanced-calling/initiate-enhanced`, {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify({
                        tenant_api_key: this.config.apiKey,
                        phone_number: phoneNumber,
                        purpose: 'Widget support call - AI Assistant with database access',
                        client_name: clientName
                    })
                });

                const responseData = await response.json();

                if (response.ok && responseData.success) {
                    if (callStatusEl) callStatusEl.textContent = 'Connected';
                    this.callInProgress = true;
                    this.currentCallId = responseData.call_id;
                    this.addMessage(`📞 AI-powered call initiated to ${phoneNumber}. Our AI assistant can answer questions about your data in real-time.`, 'bot');

                    // If Twilio is configured, phone will ring
                    if (responseData.twilio_call_sid) {
                        this.addMessage(`Your phone is ringing! Call ID: ${responseData.call_id}`, 'bot');
                    } else if (responseData.join_url) {
                        // If no Twilio, provide join URL for browser-based call
                        this.addMessage(`Join the call here: ${responseData.join_url}`, 'bot');
                    }
                } else {
                    const errorMsg = responseData.message || responseData.detail || 'Unable to connect';
                    if (callStatusEl) callStatusEl.textContent = errorMsg;
                    this.addMessage(`❌ Unable to initiate call: ${errorMsg}`, 'bot');

                    setTimeout(() => {
                        modal.classList.remove('show');
                    }, 3000);
                }
            } catch (error) {
                if (callStatusEl) callStatusEl.textContent = 'Connection error';
                this.addMessage(`❌ Call service unavailable: ${error.message}`, 'bot');

                setTimeout(() => {
                    modal.classList.remove('show');
                }, 3000);
            }
        }

        endCall() {
            const modal = document.getElementById('acwCallModal');
            if (modal) modal.classList.remove('show');
            
            this.callInProgress = false;
            this.isMuted = false;

            // Reset mute button
            const muteBtn = document.getElementById('acwCallMute');
            if (muteBtn) {
                muteBtn.textContent = '🎤';
                muteBtn.style.background = '#f3f4f6';
                muteBtn.style.color = '#374151';
            }

            this.addMessage('📞 Call ended. Thank you for contacting us.', 'bot');
        }

        toggleMute() {
            this.isMuted = !this.isMuted;
            const btn = document.getElementById('acwCallMute');
            if (btn) {
                btn.textContent = this.isMuted ? '🔇' : '🎤';
                btn.style.background = this.isMuted ? '#ef4444' : '#f3f4f6';
                btn.style.color = this.isMuted ? 'white' : '#374151';
            }
        }
    }

    // Export for React component usage
    // @ts-ignore
    window.AdvancedChatWidget = AdvancedChatWidget;
})();
/* eslint-enable no-undef */

// React Component Wrapper
const AdvancedChatWidgetComponent = () => {
    const widgetInstanceRef = useRef(null);

    useEffect(() => {
        // Check login status and initialize widget
        const checkLoginAndInit = async () => {
            try {
                // Get user info to check login status
                const userData = await getValue();
                
                // Only show widget if user is logged in
                if (userData && userData.isloggedin === true) {
                    // Extract JWT token from user data
                    const jwtToken = userData?.token || null;
                    
                    // Get API URL and API key from constants
                    const apiUrl = chatApiUrl;
                    const apiKey = chatApiKey;
                    
                    // Initialize widget only if not already initialized
                    // @ts-ignore
                    if (window.AdvancedChatWidget && !widgetInstanceRef.current) {
                        // @ts-ignore
                        widgetInstanceRef.current = new window.AdvancedChatWidget({
                            apiKey: apiKey,
                            apiUrl: apiUrl,
                            title: 'AI Assistant',
                            welcomeMessage: userData?.chatWelcomeMessage || null,
                            jwtToken: jwtToken
                        });
                    }
                } else {
                    // User is not logged in - destroy widget if it exists
                    if (widgetInstanceRef.current && typeof widgetInstanceRef.current.destroy === 'function') {
                        widgetInstanceRef.current.destroy();
                        widgetInstanceRef.current = null;
                    }
                }
            } catch (error) {
                console.error('Error checking login status for Advanced Chat Widget:', error);
                // On error, destroy widget to be safe
                if (widgetInstanceRef.current && typeof widgetInstanceRef.current.destroy === 'function') {
                    widgetInstanceRef.current.destroy();
                    widgetInstanceRef.current = null;
                }
            }
        };

        // Check login status on mount
        checkLoginAndInit();

        // Cleanup on unmount
        return () => {
            if (widgetInstanceRef.current && typeof widgetInstanceRef.current.destroy === 'function') {
                widgetInstanceRef.current.destroy();
                widgetInstanceRef.current = null;
            }
        };
    }, []);

    // This component doesn't render anything visible - it injects HTML into body
    return null;
};

export default AdvancedChatWidgetComponent;